import { useEffect, useRef, useState } from 'react';
import { useGoogleAuth } from '../store/googleAuthStore';
import './GoogleSignIn.css';

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (options: {
                        client_id: string;
                        callback: (response: { credential?: string }) => void;
                    }) => void;
                    renderButton: (element: HTMLElement, options: Record<string, unknown>) => void;
                    disableAutoSelect: () => void;
                };
            };
        };
    }
}

// This identifier is intentionally public and embedded in the web build.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_SCRIPT_ID = 'google-identity-services';

export function GoogleSignIn() {
    const { isSignedIn, user, signInWithCredential, signOut } = useGoogleAuth();
    const buttonRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isSignedIn || !buttonRef.current) return;
        if (!GOOGLE_CLIENT_ID) {
            setError('Family cloud sign-in is not configured.');
            return;
        }

        let cancelled = false;
        const renderGoogleButton = () => {
            if (cancelled || !buttonRef.current || !window.google) return;
            buttonRef.current.replaceChildren();
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: ({ credential }) => {
                    if (!credential) {
                        setError('Google did not return a sign-in credential.');
                        return;
                    }
                    void signInWithCredential(credential).then(success => {
                        setError(success ? '' : 'Could not connect this device to the family cloud account.');
                    });
                }
            });
            window.google.accounts.id.renderButton(buttonRef.current, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                shape: 'pill',
                text: 'continue_with',
                width: Math.min(320, buttonRef.current.clientWidth || 320)
            });
        };

        const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
        if (window.google) {
            renderGoogleButton();
        } else if (existingScript) {
            existingScript.addEventListener('load', renderGoogleButton, { once: true });
        } else {
            const script = document.createElement('script');
            script.id = GOOGLE_SCRIPT_ID;
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = renderGoogleButton;
            script.onerror = () => setError('Could not load Google sign-in. Check the connection and try again.');
            document.head.appendChild(script);
        }

        return () => {
            cancelled = true;
            existingScript?.removeEventListener('load', renderGoogleButton);
        };
    }, [isSignedIn, signInWithCredential]);

    if (isSignedIn && user) {
        return (
            <div className="google-signin google-signin--signed-in">
                <img src={user.picture || ''} alt="Profile" className="google-signin__avatar" />
                <div className="google-signin__info">
                    <span className="google-signin__email">{user.email}</span>
                    <span className="google-signin__premium">✓ Family cloud sync active</span>
                </div>
                <button
                    onClick={() => {
                        window.google?.accounts.id.disableAutoSelect();
                        void signOut();
                    }}
                    className="google-signin__signout"
                >
                    Sign Out
                </button>
            </div>
        );
    }

    return (
        <div className="google-signin__connect">
            <div ref={buttonRef} className="google-signin__gis-button" />
            {error && <p className="google-signin__error" role="alert">{error}</p>}
        </div>
    );
}
